import React, { useState, useRef } from 'react';
import { X, UserPlus, Mail, User, Copy, Check, Send, KeyRound, Upload, Trash2 } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { api } from '../../lib/api';
import type { WorkspaceInvitation, WorkspaceRole, CreateInvitationRequest } from '@sahaj/types';
import { UserAvatar } from '../common/UserAvatar';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose }) => {
  const { currentWorkspace } = useWorkspace();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('MEMBER');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdInvitation, setCreatedInvitation] = useState<WorkspaceInvitation | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  if (!isOpen || !currentWorkspace) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError(null);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
      setAvatarPreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);

      let uploadedAvatarObjectKey: string | undefined;
      if (avatarFile) {
        try {
          const res = await api.workspaces.uploadEmployeePhoto(currentWorkspace.id, avatarFile);
          uploadedAvatarObjectKey = res.tempObjectKey;
        } catch (photoErr: any) {
          const photoMsg = photoErr?.response?.data?.error?.message || photoErr?.response?.data?.message || photoErr?.message;
          setError(`Failed to upload employee photo: ${photoMsg || 'Server error'}. Invitation was cancelled.`);
          setIsSubmitting(false);
          return;
        }
      }

      const payload: CreateInvitationRequest = {
        email: email.trim().toLowerCase(),
        role,
        ...(uploadedAvatarObjectKey ? { avatarObjectKey: uploadedAvatarObjectKey } : {}),
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      };
      const inv = await api.workspaces.invite(currentWorkspace.id, payload);
      setCreatedInvitation(inv);
      setEmail('');
      setDisplayName('');
      handleRemoveAvatar();
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to add employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inviteToken = createdInvitation?.rawToken || createdInvitation?.id || '';
  const inviteUrl = `${window.location.origin}/invite?token=${encodeURIComponent(inviteToken)}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyPassword = () => {
    if (createdInvitation?.temporaryPassword) {
      navigator.clipboard.writeText(createdInvitation.temporaryPassword);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-base text-foreground">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <UserPlus className="h-4 w-4" />
            </div>
            <span>Add Employee to {currentWorkspace.name}</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs font-medium">
              {error}
            </div>
          )}

          {createdInvitation ? (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                {createdInvitation.temporaryPassword
                  ? `Account provisioned for ${createdInvitation.invitedName || createdInvitation.email}!`
                  : `Invitation sent to ${createdInvitation.email}!`}
              </div>

              {createdInvitation.temporaryPassword && (
                <div className="p-3.5 bg-muted/60 border border-border rounded-xl space-y-2">
                  <div className="flex items-center justify-between text-xs font-semibold text-foreground">
                    <div className="flex items-center gap-1.5 text-primary">
                      <KeyRound className="h-3.5 w-3.5" />
                      <span>Generated Temporary Password</span>
                    </div>
                    <button
                      onClick={handleCopyPassword}
                      className="text-xs text-primary hover:underline font-semibold inline-flex items-center gap-1"
                    >
                      {copiedPassword ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      <span>{copiedPassword ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <div className="p-2 bg-background border border-border rounded-lg font-mono text-sm text-foreground tracking-wider font-bold select-all">
                    {createdInvitation.temporaryPassword}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    This temporary password has been emailed to the employee. They will be required to change it upon first login.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Activation Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={inviteUrl}
                    className="flex-1 h-10 px-3 bg-background border border-border rounded-xl text-xs font-mono select-all focus:outline-none"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-3 bg-primary text-primary-foreground text-xs font-semibold rounded-xl hover:bg-primary/90 flex items-center gap-1.5 transition"
                  >
                    {copiedLink ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    <span>{copiedLink ? 'Copied' : 'Copy Link'}</span>
                  </button>
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setCreatedInvitation(null)}
                  className="px-4 py-2 bg-muted text-foreground text-sm font-semibold rounded-xl hover:bg-muted/80 transition"
                >
                  Add Another Employee
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Employee Full Name (Optional)</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Rahul Sharma"
                    className="w-full h-10 pl-9 pr-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                  />
                </div>
              </div>

              {/* Optional Profile Photo upload */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Profile Photo <span className="text-muted-foreground font-normal">(Optional)</span>
                </label>
                <div className="flex items-center gap-3.5 p-3 rounded-xl bg-muted/40 border border-border">
                  <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 border border-border shadow-2xs">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <UserAvatar displayName={displayName || 'Employee'} size="md" className="w-full h-full rounded-xl" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-2.5 py-1 bg-background hover:bg-muted text-foreground border border-border rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition shadow-2xs"
                      >
                        <Upload className="h-3 w-3" />
                        <span>{avatarPreview ? 'Change' : 'Upload'}</span>
                      </button>

                      {avatarPreview && (
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition"
                          title="Remove Photo"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Work Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="rahul@company.com"
                    className="w-full h-10 pl-9 pr-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Workspace Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as WorkspaceRole)}
                  className="w-full h-10 px-3 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                >
                  <option value="MEMBER">Member (Standard Access)</option>
                  <option value="ADMIN">Admin (Manage Members & Channels)</option>
                </select>
              </div>

              <p className="text-xs text-muted-foreground">
                The employee account will be provisioned with a secure temporary password and an activation link sent via email.
              </p>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!email.trim() || isSubmitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-primary/90 transition shadow-sm disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  <span>{isSubmitting ? 'Provisioning...' : 'Provision & Invite'}</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
